<template>
	<form>
		<div class="draft-status-bar">
			<div v-if="!formSaving && hasErrors" class="draft-status bad-text"><span class="fa fa-exclamation-triangle"></span> Please correct errors above</div>
			<div v-else class="draft-status">{{ hasFormChanged ? 'You have unsaved changes' : (form.new ? '' : 'All changes saved') }}</div>
			<div v-if="!formSaving" class="draft-button"><a href="/commerce/products/list" @click.prevent="() => hasFormChanged ? onSubmit() : null" class="button " :class="{ disabled: !hasFormChanged }" :disabled="!hasFormChanged"><div class="hover-title">Save</div><span class="fa fa-save"></span></a></div>
			<div v-if="formSaving" class="draft-button"><a class="button disabled"><div class="hover-title">Processing</div><span class="fa fa-sync fa-spin"></span></a></div>
		</div>

		<form-errors v-if="hasErrors" :errors="errors"></form-errors>

		<field-errors v-if="submitAttempted" :form="$v.form" field="title"></field-errors>
		<div class="input-container">
			<div class="input-title">*Product Title</div>
			<div class="input-box"><input type="text" v-model="form.title"></div>
		</div>
		<field-errors v-if="submitAttempted" :form="$v.form" field="slug"></field-errors>
		<div class="input-container">
			<div class="input-title">*Product Slug</div>
			<div class="input-box"><slug-field v-model="form.slug" :base-field="form.title" separator="-"></slug-field></div>
		</div>
		<field-errors v-if="submitAttempted" :form="$v.form" field="categories"></field-errors>
		<div class="input-container">
			<div class="input-title">Categories</div>
			<div class="input-box">
				<selectize v-model="form.categories" :settings="categorySettings" placeholder="Select Categories" multiple></selectize>
			</div>
			<div class="input-link">
				<router-link to="/commerce/products/categories"><span class="fa fa-cog"></span>Manage Categories</router-link>
			</div>
		</div>
		<field-errors v-if="submitAttempted" :form="$v.form" field="description"></field-errors>
		<div class="conjoin-input-containers">
			<div class="input-container">
				<div class="input-title">*Product Description</div>
			</div>
			<div class="input-container">
				<div class="input-box">
					<product-description-field v-model="form.description"></product-description-field>
				</div>
			</div>
		</div>

		{{ /* Curated Module Fields */ }}
		<component
			v-for="field in visibleFields" :key="`v_${field.id}`"
			:ref="field.slug"
			v-model="form"
			:form-id="formId"
			:field="field"
			:module="moduleData.module"
			:tags="tags['v_' + field.id] || []"
			:options="associatedModules['v_' + field.id] || []"
			:is="getFieldComponent(field)"
			:show-errors="submitAttempted"
		/>


		<field-errors v-if="submitAttempted" :form="$v.form" field="tracks_inventory"></field-errors>
		<div class="input-container">
			<div class="input-checkbox">
				<span class="checkmark-container">
					<label class="checkbox">
						<input type="checkbox" id="tracks_inventory" v-model="form.tracks_inventory">
						<span class="checkmark"></span>
					</label>
				</span>
				<label for="tracks_inventory" class="input-checkbox-text">Use inventory tracking to determine product availability</label>
			</div>
		</div>

		<product-variants-form v-model="form" :v="$v" :submit-attempted="submitAttempted"></product-variants-form>

		<div class="buffer large"></div>

		<product-photos-form v-model="form" :v="$v" :submit-attempted="submitAttempted"></product-photos-form>

		<field-errors v-if="submitAttempted" :form="$v.form" field="is_active"></field-errors>
		<div class="input-container">
			<div class="input-checkbox">
				<span class="checkmark-container">
					<label class="checkbox">
						<input type="checkbox" id="is_active" v-model="form.is_active">
						<span class="checkmark"></span>
					</label>
				</span>
				<label for="is_active" class="input-checkbox-text">Make this product available for purchase</label>
			</div>
		</div>

		<div class="draft-status-bar-buffer"></div>
	</form>
</template>

<script>

import _ from 'underscore';
import { required, requiredIf, minLength, decimal } from 'vuelidate/lib/validators';
import CrudForm from '@/mixins/CrudForm';
import FormMixin from '@/mixins/FormMixin';
import ProductFormMixin from '../mixins/ProductFormMixin';
import ProductVariantsForm from './ProductVariantsForm';
import ProductPhotosForm from './ProductPhotosForm';
import ProductDescriptionField from './ProductDescriptionField';

export default {

	props: {
		value: {
			type: Object,
			required: true,
		},
		originalForm: Object,
		categories: {
			type: Array,
			default: () => ([]),
		},
		moduleData: {
			type: Object,
			required: true,
		},
		serverErrors: Object,
		create: {
			type: Boolean,
			default: false
		},
	},

	mixins: [CrudForm, FormMixin, ProductFormMixin],

	data() {
		return {
			form: this.value,
			productId: this.value.id,
			formId: this.value.hasOwnProperty('id') ? this.value.id : _.uniqueId(),
		};
	},

	computed: {

		hasFormChanged() {
			return JSON.stringify(this.form) !== JSON.stringify(this.originalForm);
		},

		categorySettings() {
			return {
				persist: true,
				options: this.categories.map(category => ({
					value: category.id,
					text: category.name
				})),
				create: (input) => {
					return {
						text: input,
						value: input
					};
				},
				createOnBlur: true,
			};
		},
	},

	validations: {
		form: {
			title: { required },
			slug: { required },
			description: { required },
			variants: {
				required,
				minLength: minLength(1),
				// $each: {
				// 	retail_price: { required },
				// 	wholesale_price: { required: requiredIf(function() { return this.hasWholesalePricing; }) },
				// 	inventory: {
				// 		required: requiredIf(() => requiredIf(function() { return this.tracksInventory; })),
				// 		decimal,
				// 	},
				// },
				// groups: {
				// 	required: requiredIf(() => requiredIf(function() { return this.hasVariants; })),
				// }
			},
			variant_groups: {
				required: requiredIf(function() { return this.hasVariants; }),
				// TODO - This each doesn't work. And even if it did, we can't just check the name is required on index 0. It's got to be on first index that isn't pendingDelete
				// minLength: 1,
				// $each: {
				// 	name: {
				// 		required: requiredIf(function(nestedModel) { return this.form.variant_groups.indexOf(nestedModel) === 0; }),
				// 	}
				// },
			}
		}
	},

	watch: {

		serverErrors(newValue) {
			this.errors = newValue;
		},

	},

	methods: {

		onSubmit() {
			this.submitAttempted = true;
			if (!this.formIsValid()) {
				return;
			}

			this.submit(async () => {
				const { product, variantMapping } = await this.saveProduct();
				this.productId = product.id;
				try {
					await this.uploadFiles(variantMapping);

					this.$emit('saved');
				} catch (e) {
					this.$store.dispatch('errorToast', 'There was a problem saving all your photos');
					this.$router.replace('/commerce/products/' + this.productId + '/edit');
				}
			});
		},

		async saveProduct() {
			let data;
			if (this.create) {
				// New Product
				const response = await this.$api.post('/commerce/products', this.form);
				data = response.data;
			} else {
				// Update Product
				const response = await this.$api.put('/commerce/products/' + this.form.id, this.form);
				data = response.data;
			}

			return {
				product: data.data,
				variantMapping: data.newVariantMapping,
			};
		},

		/*
		  Submits the files to the server
		*/
		async uploadFiles(variantMapping) {
			const uploads = [];

			const updates = [];

			/*
			  Iteate over any file sent over appending the files
			  to the form data.
			*/
			(this.form.files || []).forEach((file, key) => {
				if (typeof variantMapping[file.variant_id] !== 'undefined') {
					file.variant_id = variantMapping[file.variant_id];
				}

				if (file.isNew) {
					uploads.push(this.saveNewPhoto(file, key));
				} else {
					file.order = key;
					updates.push(file);
				}
			});

			if (updates.length > 0) {
				uploads.push(this.updateExistingPhotos(updates));
			}

			try {
				await Promise.all(uploads);
			} catch(e) {}
		},

		uploadSuccess(key, newPhoto) {
			this.$set(this.form.files[ key ], 'result', true);
			this.removeFile(key); // Comment this out if you want a 'upload complete state' - see setTimeout below
			this.$forceUpdate();
			this.$emit('file-added', newPhoto);
		},

		saveNewPhoto(file, order) {
			let formData = new FormData();
			formData.append('file', file.transfer);
			formData.append('order', order);
			formData.append('caption', file.caption);
			formData.append('variant_id', file.variant_id || 0);

			this.form.files[ order ] = {
				...this.form.files[ order ],
				result: null,
				started: true,
				processing: false,
			};

			return this.$api.post(`/commerce/products/${ this.productId }/upload`,
				formData,
				{
					headers: { 'Content-Type': 'multipart/form-data' },
					onUploadProgress: (progressEvent) => {
						this.$set(this.form.files[ order ], 'progress', parseInt(Math.round((progressEvent.loaded * 100) / progressEvent.total)));
						this.$set(this.form.files[ order ], 'processing', this.form.files[ order ].progress === 100);
						this.$forceUpdate();
					},
				},
			);
		},

		updateExistingPhotos(files) {
			return this.$api.put(`/commerce/products/${ this.productId }/photos`, { files });
		}

	},

	components: {
		ProductDescriptionField,
		ProductPhotosForm,
		ProductVariantsForm
	},

};

</script>
